/**
 * Navegación inferior.
 *
 * `docs/06` §"Breakpoints" la da por supuesta desde el ancho base, pero no
 * existía: la única forma de llegar a "tus lugares" era un enlace de texto
 * en la cabecera del mapa, y al perfil no se llegaba de ninguna. Una
 * pantalla a la que no se puede navegar es una pantalla que no existe.
 *
 * **Tres destinos y no cinco.** Colecciones y descubrimiento son features
 * del MVP que todavía no están; una pestaña que lleva a nada es peor que
 * una pestaña de menos. Cuando existan, entran aquí.
 *
 * Los iconos son SVG en línea —outline de 1.5px, como pide `docs/06` §5—
 * y no una fuente de iconos: son tres, y una dependencia entera para tres
 * glifos que además hay que cargar antes de que la barra se vea es un mal
 * negocio.
 */

import type { ReactNode } from 'react';

export type Destino = '/' | '/mis-lugares' | '/perfil';

export interface BottomNavProps {
  activo: Destino;
  onNavegar: (destino: Destino) => void;
}

/** Altura de la barra sin contar la zona segura del dispositivo. */
const ALTO = '3.5rem';

export function BottomNav({ activo, onNavegar }: BottomNavProps) {
  return (
    <nav
      aria-label="Principal"
      style={{
        position: 'fixed',
        insetInline: 0,
        bottom: 0,
        zIndex: 'var(--gu-z-nav)' as unknown as number,
        background: 'var(--gu-surface-app)',
        borderTop: '1px solid var(--gu-border-subtle)',
        display: 'flex',
        /*
         * La zona segura SUMADA al alto, no sustituyéndolo: en un iPhone
         * con barra de gestos, sin esto los botones quedan debajo del
         * indicador y no se pueden tocar (`docs/06` §"Zona segura").
         */
        paddingBottom: 'var(--gu-safe-bottom)',
        // 32rem = el ancho máximo de `Pantalla`. En escritorio la barra
        // ocupa todo el ancho pero sus botones se alinean con el contenido.
        justifyContent: 'center',
      }}
    >
      <div style={{ display: 'flex', width: '100%', maxWidth: '32rem' }}>
        <Pestana
          destino="/"
          etiqueta="Mapa"
          activo={activo === '/'}
          onNavegar={onNavegar}
          icono={<IconoMapa />}
        />
        <Pestana
          destino="/mis-lugares"
          etiqueta="Tus lugares"
          activo={activo === '/mis-lugares'}
          onNavegar={onNavegar}
          icono={<IconoLugares />}
        />
        <Pestana
          destino="/perfil"
          etiqueta="Perfil"
          activo={activo === '/perfil'}
          onNavegar={onNavegar}
          icono={<IconoPerfil />}
        />
      </div>
    </nav>
  );
}

function Pestana({
  destino,
  etiqueta,
  activo,
  onNavegar,
  icono,
}: {
  destino: Destino;
  etiqueta: string;
  activo: boolean;
  onNavegar: (d: Destino) => void;
  icono: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => onNavegar(destino)}
      // `current="page"` y no solo el color: quien navega con lector de
      // pantalla no ve el verde, y sin esto no sabría dónde está.
      aria-current={activo ? 'page' : undefined}
      style={{
        flex: 1,
        minHeight: ALTO,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        /*
         * `--gu-primary-text` y no `--gu-primary`: el verde de marca da
         * 3.24:1 sobre cream y falla AA como texto (ADR-014). Aquí el
         * texto mide 12px, donde la regla es más estricta, no menos.
         */
        color: activo ? 'var(--gu-primary-text)' : 'var(--gu-text-secondary)',
        fontFamily: 'inherit',
        fontSize: 'var(--gu-text-caption)',
        fontWeight: activo ? 600 : 400,
        padding: 0,
      }}
    >
      <span aria-hidden="true" style={{ display: 'block', lineHeight: 0 }}>
        {icono}
      </span>
      {etiqueta}
    </button>
  );
}

/* ─────────────────────────────── Iconos ─────────────────────────────── */

/*
 * `docs/06` §5: outline, trazo 1.5px, esquinas ligeramente redondeadas.
 * `currentColor` para que hereden el estado activo del botón sin duplicar
 * la lógica de color.
 */

const COMUN = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function IconoMapa() {
  return (
    <svg {...COMUN}>
      <path d="M9 4 3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5 9 4Z" />
      <path d="M9 4v13M15 6.5v13" />
    </svg>
  );
}

function IconoLugares() {
  return (
    <svg {...COMUN}>
      <path d="M12 21s-6.5-5.4-6.5-10a6.5 6.5 0 1 1 13 0c0 4.6-6.5 10-6.5 10Z" />
      <circle cx="12" cy="11" r="2.25" />
    </svg>
  );
}

function IconoPerfil() {
  return (
    <svg {...COMUN}>
      <circle cx="12" cy="8" r="3.75" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}
